import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLifeRing,
  faEnvelope,
  faHeadset,
  faPhone,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { videoTutorials, contactCards } from "../devdata/constants/supportData";
import { ContactCard, VideoTutorial } from "../components";
import { ThemeProvider } from "../components/ThemeProvider";
import {
  Mail,
  Phone,
  MessageCircle,
  HelpCircle,
  LifeBuoy,
  Video,
} from "lucide-react";
import { Header, Footer } from "./home/index";
import HelpSupport from "./helpSupport/index";

// Original component without any changes
export function OriginalHelpSupport() {
  return (
    <div className="flex flex-col bg-darkMode-background min-h-screen p-6">
      <div className="flex flex-wrap gap-6 items-start w-full">
        <div className="flex flex-col pb-10 rounded-xl w-full">
          <div className="flex items-center gap-1 text-2xl font-semibold text-darkMode-text">
            {/* <Link to="/Dashboard"> */}
            {/* <FontAwesomeIcon icon={faArrowLeft} /> */}
            {/* </Link> */}
            <span>Help & Contact Us</span>
          </div>
          <span className="span-sm text-darkMode-subText">
            Find the support you need to manage stocks efficiently.
          </span>
        </div>
      </div>
      {/* Contact Cards Section */}
      <section className="flex md:flex-row lg:flex-row gap-4 px-8 mt-8 w-full flex-wrap">
        {contactCards.map((card, index) => (
          <ContactCard key={index} {...card} />
        ))}
      </section>
    </div>
  );
}

// Themed version with market-teal styling - keeping EXACT same structure as original
const SupportContent = ({ themeVariant }) => {
  const isMarketing = themeVariant === "marketing";

  return (
    <div
      className={`flex flex-col ${isMarketing ? "bg-black" : "bg-gray-50"
        } min-h-screen p-14 pt-24 relative`}
    >
      {/* Subtle gradient background */}
      {isMarketing && (
        <>
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black to-black/90 pointer-events-none"></div>
          <div className="absolute inset-0 bg-grid-pattern bg-[size:20px_20px] pointer-events-none opacity-[0.03]"></div>
          <div className="absolute top-40 -left-20 w-72 h-72 bg-market-teal/20 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="absolute top-1/2 right-0 w-64 h-64 bg-market-teal/10 rounded-full blur-[100px] pointer-events-none"></div>
        </>
      )}

      <div className="relative z-10 flex flex-wrap gap-6 items-start w-full">
        <div className="flex flex-col pb-10 rounded-xl w-full">
          <div className="flex items-center gap-1 text-2xl font-semibold text-white">
            <span>
              {isMarketing && (
                <span className="text-market-teal drop-shadow-[0_0_6px_rgba(0,255,143,0.3)]">
                  Help &{" "}
                </span>
              )}
              {!isMarketing && "Help & "}
              {isMarketing && (
                <span className="text-gradient-white">Contact Us</span>
              )}
              {!isMarketing && "Contact Us"}
            </span>
          </div>
          <span
            className={`${isMarketing ? "text-white/70" : "text-gray-600"}`}
          >
            Find the support you need to manage stocks efficiently.
          </span>
        </div>
      </div>
      {/* Contact Cards Section - same structure as original */}
      <section className="relative z-10 flex flex-wrap justify-start gap-6 px-8 mt-8 w-full">
        {contactCards.map((card, index) => (
          <div
            key={index}
            className={`w-full sm:w-[350px] ${isMarketing
              ? "intellectia-card p-8 hover:shadow-[0_0_25px_rgba(0,0,0,0.3),_0_0_2px_rgba(0,255,143,0.2)] transition-all duration-300"
              : "bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-all"
              }`}
          >
            <div className="flex items-center mb-6">
              <div
                className={`${isMarketing
                  ? "bg-market-teal/20 p-3 rounded-lg"
                  : "bg-blue-100 p-3 rounded-lg"
                  }`}
              >
                <FontAwesomeIcon
                  icon={card.icon}
                  className={
                    isMarketing
                      ? "text-market-teal text-2xl"
                      : "text-blue-500 text-2xl"
                  }
                />
              </div>
              <h3
                className={
                  isMarketing
                    ? "text-xl font-bold text-white ml-4"
                    : "text-xl font-semibold text-gray-800 ml-4"
                }
              >
                {card.title}
              </h3>
            </div>
            <p
              className={
                isMarketing ? "text-white/80 mb-6" : "text-gray-600 mb-6"
              }
            >
              {card.description}
            </p>
            <a
              href={card.contact}
              target="_blank"
              rel="noopener noreferrer"
              className={
                isMarketing
                  ? "px-6 py-3 rounded-lg bg-market-teal/20 text-market-teal border border-market-teal/50 hover:bg-market-teal/30 transition-all duration-300 inline-flex items-center gap-2 group"
                  : "px-6 py-3 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all duration-300 inline-flex items-center gap-2"
              }
            >
              Contact
              <FontAwesomeIcon
                icon={faArrowRight}
                className={`transform group-hover:translate-x-1 transition-transform duration-300 ${isMarketing ? "text-market-teal" : "text-blue-600"
                  }`}
              />
            </a>
          </div>
        ))}
      </section>
    </div>
  );
};

// Marketing version
export const MarketingSupport = () => {
  return (
    <ThemeProvider variant="marketing">
      <SupportContent themeVariant="marketing" />
    </ThemeProvider>
  );
};

// Dashboard version - directly use the original HelpSupport component
export const DashboardSupport = () => {
  return <HelpSupport />;
};

// Default export for the route
const Support = ({ variant = "marketing" }) => {
  return variant === "marketing" ? <MarketingSupport /> : <DashboardSupport />;
};

export default Support;
